import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  SidebarProvider,
  SidebarTrigger,
} from "@/project/karuna/components/ui/sidebar"; // @ts-ignore - sidebar is not a valid module
import { AdminSidebar } from "@/project/karuna/components/admin/AdminSidebar"; // @ts-ignore - AdminSidebar is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/project/karuna/components/ui/dropdown-menu"; // @ts-ignore - dropdown-menu is not a valid module
import { clearAuthData, getAccessToken, getUserInfo } from "@/lib/storeManager";
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { isAdminPortalUser } from "@/project/karuna/utils/adminPermissionUtils";

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const getLink = useKarunaLink();
  const userInfo = getUserInfo();
  const namePart = [userInfo?.first_name, userInfo?.last_name]
    .filter(Boolean)
    .join(" ");
  const displayName = namePart || (userInfo?.email ?? "Admin");

  // ADMIN PORTAL: Require admin login (token + admin user profile)
  useEffect(() => {
    const checkAuth = () => {
      const accessToken = getAccessToken();
      const currentUser = getUserInfo();
      const isAdminSession = accessToken && isAdminPortalUser(currentUser);
      if (!isAdminSession) {
        navigate(getLink("/admin/login"));
      }
    };

    checkAuth();
    const handleStorageChange = (e: StorageEvent) => {
      if (
        (e.key === "accessToken" && e.newValue === null) ||
        (e.key === "userInfo" && e.newValue === null)
      ) {
        navigate(getLink("/admin/login"));
      }
    };
    const onFocus = () => {
      checkAuth();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        checkAuth();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = setInterval(checkAuth, 60_000);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [navigate, getLink]);

  const handleLogout = () => {
    clearAuthData();
    navigate(getLink("/admin/login"));
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />

        <div className="flex flex-1 flex-col">
          <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-border bg-background px-6">
            <SidebarTrigger />

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2">
                    <User className="h-5 w-5" />
                    <span className="hidden md:inline">{displayName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleLogout}>
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="flex-1 bg-muted/10 p-6">{children}</main>
        </div>
      </div>
    </SidebarProvider>
  );
}
